import { GoogleMap, InfoWindow, Marker, Polyline, Rectangle } from '@react-google-maps/api';
import * as React from 'react';

import { loadGoogleMaps } from '../utils/googleMaps';
import { STOP_WARNING_COLOR, stopHasWarning } from './utils';

const DEFAULT_HEIGHT = 500;
const DEFAULT_CENTER = { lat: 0, lng: 0 };
const SINGLE_STOP_ZOOM = 15;
const EMPTY_ZOOM = 2;

const buildPinIcon = (color, label, hasWarning) => {
    const safeColor = color || '#007AFF';
    const warningBadge = hasWarning
        ? `<circle cx="30" cy="8" r="7" fill="${STOP_WARNING_COLOR}" stroke="#ffffff" stroke-width="1.5"/>
  <text x="30" y="12" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" font-weight="bold" fill="#ffffff">!</text>`
        : '';
    const width = hasWarning ? 38 : 34;
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="42" viewBox="0 0 ${width} 42">
  <g transform="translate(17 17) rotate(-45)">
    <rect x="-13" y="-13" width="26" height="26"
          rx="13" ry="13"
          style="fill:${safeColor};stroke:#ffffff;stroke-width:2"/>
  </g>
  <text x="17" y="22" text-anchor="middle"
        font-family="Arial, sans-serif" font-size="13" font-weight="bold" fill="#ffffff">${label}</text>
  ${warningBadge}
</svg>`.trim();
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const buildVehicleIcon = (heading, lineColor) => {
    const color = lineColor || '#007AFF';
    const rotation = typeof heading === 'number' ? heading : 0;
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
  <g transform="rotate(${rotation} 16 16)">
    <circle cx="16" cy="16" r="14" fill="${color}" stroke="#ffffff" stroke-width="2"/>
    <polygon points="16,4 22,20 16,16 10,20" fill="#ffffff"/>
  </g>
</svg>`.trim();
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const STATUS_LABELS = {
    moving: 'Moving',
    stopped: 'Stopped',
    idle: 'Idle',
    offline: 'Offline',
};

const formatAge = (ts) => {
    if (!ts) return 'Unknown';
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
};

// Parse a KML coordinates string ("lng,lat,alt lng,lat,alt ...") into [{lat,lng}, ...]
const parseCoordString = (text) => {
    if (!text) return [];
    return text
        .trim()
        .split(/\s+/)
        .map((tuple) => {
            const parts = tuple.split(',');
            const lng = parseFloat(parts[0]);
            const lat = parseFloat(parts[1]);
            if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
            return { lat, lng };
        })
        .filter(Boolean);
};

// Extract LineString paths from a KML string. Each result is an array of {lat,lng}.
const parseKmlLineStrings = (kmlText) => {
    if (!kmlText || typeof kmlText !== 'string') return [];
    try {
        const doc = new DOMParser().parseFromString(kmlText, 'application/xml');
        if (doc.querySelector('parsererror')) return [];
        const result = [];
        // LineString geometries
        doc.querySelectorAll('LineString > coordinates').forEach((node) => {
            const path = parseCoordString(node.textContent);
            if (path.length >= 2) result.push(path);
        });
        // Polygon outer rings (some KML routes are encoded as polygons)
        doc.querySelectorAll('Polygon > outerBoundaryIs > LinearRing > coordinates').forEach((node) => {
            const path = parseCoordString(node.textContent);
            if (path.length >= 2) result.push(path);
        });
        return result;
    } catch (e) {
        // Malformed KML — render nothing
        return [];
    }
};

const BusStopsMap = (props) => {
    const { googleApiKey, stops, vehicles, routeColors, showStops = true, color, kml, kmlUrl, onStopClick, onViewportDirty, resetViewportKey, height, routeBoundingBox, routeBoundingBoxLabel, routeSetBoundingBox, routeSetBoundingBoxLabel } = props;

    const [mapInstance, setMapInstance] = React.useState(null);
    const [googleApi, setGoogleApi] = React.useState(null);
    const [fetchedKml, setFetchedKml] = React.useState(null);
    const [selectedVehicle, setSelectedVehicle] = React.useState(null);
    const [bboxTooltip, setBboxTooltip] = React.useState(null); // { lat, lng, text }

    // Track whether the user has manually panned/zoomed the map.
    const userInteracted = React.useRef(false);
    // Suppress interaction detection briefly after programmatic fitBounds.
    const suppressInteraction = React.useRef(false);

    // If a KML URL is provided (from the backend) and we don't already have inline
    // KML text, fetch it once and parse client-side. Google's KmlLayer is unsuitable
    // because it requires a publicly reachable URL fetched by Google's tile servers.
    React.useEffect(() => {
        if (kml || !kmlUrl) {
            setFetchedKml(null);
            return;
        }
        let cancelled = false;
        fetch(kmlUrl)
            .then((res) => (res.ok ? res.text() : null))
            .then((text) => {
                if (!cancelled) setFetchedKml(text || null);
            })
            .catch(() => {
                if (!cancelled) setFetchedKml(null);
            });
        return () => {
            cancelled = true;
        };
    }, [kml, kmlUrl]);

    const effectiveKml = kml || fetchedKml;

    const containerStyle = React.useMemo(
        () => ({ width: '100%', height: typeof height === 'number' ? `${height}px` : height || `${DEFAULT_HEIGHT}px` }),
        [height]
    );

    // Build a parallel array of plottable stops, preserving the original index so
    // pin clicks can be mapped back to the unfiltered stops array used by the parent.
    const plottable = React.useMemo(() => {
        if (!Array.isArray(stops)) return [];
        return stops
            .map((link, originalIndex) => ({ link, originalIndex }))
            .filter(({ link }) => {
                const lat = link?.stop?.latitude;
                const lng = link?.stop?.longitude;
                return typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng);
            });
    }, [stops]);

    const plottableVehicles = React.useMemo(() => {
        if (!Array.isArray(vehicles)) return [];
        return vehicles.filter(
            (v) => typeof v.latitude === 'number' && typeof v.longitude === 'number' && !Number.isNaN(v.latitude) && !Number.isNaN(v.longitude)
        );
    }, [vehicles]);

    // Keep the selected vehicle's popover data fresh when positions update.
    React.useEffect(() => {
        if (!selectedVehicle || !Array.isArray(vehicles)) return;
        const fresh = vehicles.find((v) => v.id === selectedVehicle.id);
        if (fresh) {
            setSelectedVehicle(fresh);
        } else {
            setSelectedVehicle(null);
        }
    }, [vehicles]); // eslint-disable-line react-hooks/exhaustive-deps

    const kmlPaths = React.useMemo(() => parseKmlLineStrings(effectiveKml), [effectiveKml]);

    // Shared loader rather than <LoadScript>: this mount asked for no `libraries`
    // while the activity editor's asked for `places`, and two different script URLs
    // in one SPA session is what Google reports as a double-include.
    React.useEffect(() => {
        if (!googleApiKey) return undefined;
        let mounted = true;
        loadGoogleMaps(googleApiKey)
            .then(() => mounted && setGoogleApi(window.google))
            .catch((e) => console.warn('BusStopsMap: Google Maps failed to load', e));
        return () => {
            mounted = false;
        };
    }, [googleApiKey]);

    const onMapLoad = React.useCallback((m) => {
        setMapInstance(m);
    }, []);

    const collectPoints = React.useCallback(() => {
        const points = [];
        if (showStops) {
            plottable.forEach(({ link }) => points.push({ lat: link.stop.latitude, lng: link.stop.longitude }));
        }
        plottableVehicles.forEach((v) => points.push({ lat: v.latitude, lng: v.longitude }));
        kmlPaths.forEach((path) => path.forEach((p) => points.push(p)));
        return points;
    }, [showStops, plottable, plottableVehicles, kmlPaths]);

    const applyFitBounds = React.useCallback((points, bbox) => {
        if (!mapInstance || !googleApi) return;
        suppressInteraction.current = true;
        if (bbox && bbox.north != null && bbox.south != null && bbox.east != null && bbox.west != null) {
            const bounds = new googleApi.maps.LatLngBounds(
                new googleApi.maps.LatLng(bbox.south, bbox.west),
                new googleApi.maps.LatLng(bbox.north, bbox.east),
            );
            mapInstance.fitBounds(bounds);
        } else if (points.length === 0) {
            mapInstance.setCenter(DEFAULT_CENTER);
            mapInstance.setZoom(EMPTY_ZOOM);
        } else if (points.length === 1) {
            mapInstance.setCenter(points[0]);
            mapInstance.setZoom(SINGLE_STOP_ZOOM);
        } else {
            const bounds = new googleApi.maps.LatLngBounds();
            points.forEach((p) => bounds.extend(new googleApi.maps.LatLng(p.lat, p.lng)));
            mapInstance.fitBounds(bounds);
        }
        setTimeout(() => { suppressInteraction.current = false; }, 500);
    }, [mapInstance, googleApi]);

    // Re-fit bounds when the data changes, but only if the user hasn't panned/zoomed.
    React.useEffect(() => {
        if (userInteracted.current) return;
        applyFitBounds(collectPoints(), routeBoundingBox);
    }, [applyFitBounds, collectPoints, routeBoundingBox]);

    // Allow the parent to force a viewport reset via a key prop.
    const prevResetKey = React.useRef(resetViewportKey);
    React.useEffect(() => {
        if (resetViewportKey !== prevResetKey.current) {
            prevResetKey.current = resetViewportKey;
            userInteracted.current = false;
            onViewportDirty?.(false);
            applyFitBounds(collectPoints(), routeBoundingBox);
        }
    }, [resetViewportKey, applyFitBounds, collectPoints, onViewportDirty, routeBoundingBox]);

    const handleUserInteraction = React.useCallback(() => {
        if (suppressInteraction.current) return;
        if (!userInteracted.current) {
            userInteracted.current = true;
            onViewportDirty?.(true);
        }
    }, [onViewportDirty]);

    if (!googleApiKey) {
        return (
            <div style={{ ...containerStyle, background: '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#666' }}>Google Maps API key is not configured</span>
            </div>
        );
    }

    return (
        googleApi ? (
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={DEFAULT_CENTER}
                zoom={EMPTY_ZOOM}
                onLoad={onMapLoad}
                onDragEnd={handleUserInteraction}
                onZoomChanged={handleUserInteraction}
                options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
            >
                {showStops && plottable.map(({ link, originalIndex }, displayIdx) => {
                    const pinLabel = link.stop?.label || String(displayIdx + 1);
                    const hasWarning = stopHasWarning(link.stop);
                    const iconWidth = hasWarning ? 38 : 34;
                    return (
                        <Marker
                            key={`stop-${originalIndex}-${link.stop.latitude}-${link.stop.longitude}`}
                            position={{ lat: link.stop.latitude, lng: link.stop.longitude }}
                            title={link.stop.name || `Stop ${displayIdx + 1}`}
                            icon={googleApi ? {
                                url: buildPinIcon(color, pinLabel, hasWarning),
                                scaledSize: new googleApi.maps.Size(iconWidth, 42),
                                anchor: new googleApi.maps.Point(17, 38),
                            } : undefined}
                            onClick={() => onStopClick && onStopClick(originalIndex)}
                        />
                    );
                })}
                {plottableVehicles.map((v) => {
                    const lineColor = (routeColors && v.route_id != null) ? routeColors[v.route_id] : color;
                    return (
                        <Marker
                            key={`vehicle-${v.id}`}
                            position={{ lat: v.latitude, lng: v.longitude }}
                            title={v.name || 'Vehicle'}
                            icon={googleApi ? {
                                url: buildVehicleIcon(v.heading, lineColor),
                                scaledSize: new googleApi.maps.Size(32, 32),
                                anchor: new googleApi.maps.Point(16, 16),
                            } : undefined}
                            onClick={() => setSelectedVehicle(v)}
                        />
                    );
                })}
                {selectedVehicle && (
                    <InfoWindow
                        position={{ lat: selectedVehicle.latitude, lng: selectedVehicle.longitude }}
                        onCloseClick={() => setSelectedVehicle(null)}
                        options={{ pixelOffset: googleApi ? new googleApi.maps.Size(0, -16) : undefined }}
                    >
                        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, lineHeight: 1.6, minWidth: 180 }}>
                            <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 4 }}>
                                {selectedVehicle.name || `Vehicle #${selectedVehicle.id}`}
                            </div>
                            {selectedVehicle.license_plate && (
                                <div>Plate: <strong>{selectedVehicle.license_plate}</strong></div>
                            )}
                            <div>Status: {STATUS_LABELS[selectedVehicle.status] || selectedVehicle.status}</div>
                            {selectedVehicle.route_name && (
                                <div>Line: {selectedVehicle.route_name}</div>
                            )}
                            {selectedVehicle.speed != null && selectedVehicle.status === 'moving' && (
                                <div>Speed: {Math.round(selectedVehicle.speed)} km/h</div>
                            )}
                            {Array.isArray(selectedVehicle.route_hints) && selectedVehicle.route_hints.length > 0 && (
                                <div>Hints: {selectedVehicle.route_hints.join(', ')}</div>
                            )}
                            {selectedVehicle.route_assignment_ts && (
                                <div title={selectedVehicle.route_assignment_ts}>Assigned: {formatAge(selectedVehicle.route_assignment_ts)}</div>
                            )}
                            <div title={selectedVehicle.location_ts || ''}>Updated: {formatAge(selectedVehicle.location_ts)}</div>
                            <div style={{ marginTop: 6 }}>
                                <a
                                    href={`/bus-tours/vehicles/${selectedVehicle.id}`}
                                    style={{ color: '#1976d2', textDecoration: 'none' }}
                                >
                                    View vehicle details
                                </a>
                            </div>
                        </div>
                    </InfoWindow>
                )}
                {kmlPaths.map((path, idx) => (
                    <Polyline
                        key={`kml-${idx}`}
                        path={path}
                        options={{
                            strokeColor: color || '#007AFF',
                            strokeOpacity: 0.9,
                            strokeWeight: 4,
                            clickable: false,
                        }}
                    />
                ))}
                {googleApi && routeSetBoundingBox && routeSetBoundingBox.north != null && (() => {
                    // Google Maps Rectangle doesn't support dashed strokes, so we draw
                    // the route set bounding box as a Polyline with a dashed line symbol.
                    const corners = [
                        { lat: routeSetBoundingBox.north, lng: routeSetBoundingBox.west },
                        { lat: routeSetBoundingBox.north, lng: routeSetBoundingBox.east },
                        { lat: routeSetBoundingBox.south, lng: routeSetBoundingBox.east },
                        { lat: routeSetBoundingBox.south, lng: routeSetBoundingBox.west },
                        { lat: routeSetBoundingBox.north, lng: routeSetBoundingBox.west },
                    ];
                    return (
                        <Polyline
                            path={corners}
                            options={{
                                strokeColor: '#666666',
                                strokeOpacity: 0,
                                icons: [{
                                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.5, strokeWeight: 1.5, scale: 3 },
                                    offset: '0',
                                    repeat: '12px',
                                }],
                                clickable: true,
                                zIndex: 1,
                            }}
                            onMouseOver={(e) => setBboxTooltip({ lat: e.latLng.lat(), lng: e.latLng.lng(), text: routeSetBoundingBoxLabel || 'Line set bounding box' })}
                            onMouseOut={() => setBboxTooltip(null)}
                        />
                    );
                })()}
                {googleApi && routeBoundingBox && routeBoundingBox.north != null && (
                    <Rectangle
                        bounds={{
                            north: routeBoundingBox.north,
                            south: routeBoundingBox.south,
                            east: routeBoundingBox.east,
                            west: routeBoundingBox.west,
                        }}
                        options={{
                            strokeColor: color || '#007AFF',
                            strokeOpacity: 0.5,
                            strokeWeight: 1,
                            fillColor: color || '#007AFF',
                            fillOpacity: 0.05,
                            clickable: true,
                            zIndex: 2,
                        }}
                        onMouseOver={(e) => setBboxTooltip({ lat: e.latLng.lat(), lng: e.latLng.lng(), text: routeBoundingBoxLabel || 'Route bounding box' })}
                        onMouseOut={() => setBboxTooltip(null)}
                    />
                )}
                {bboxTooltip && (
                    <InfoWindow
                        position={{ lat: bboxTooltip.lat, lng: bboxTooltip.lng }}
                        options={{
                            disableAutoPan: true,
                            pixelOffset: googleApi ? new googleApi.maps.Size(0, -4) : undefined,
                        }}
                        onCloseClick={() => setBboxTooltip(null)}
                    >
                        <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12 }}>{bboxTooltip.text}</span>
                    </InfoWindow>
                )}
            </GoogleMap>
        ) : (
            // The map used to mount inside <LoadScript>, which guaranteed
            // window.google by construction; now it waits for the shared loader.
            <div style={{ ...containerStyle, background: '#e8e8e8' }} />
        )
    );
};

export default BusStopsMap;
